import { setScanStatus } from './scannerSlice'

/**
 * Find an item by SKU, label, or value across both grouped and flat arrays.
 */
const findItemBySku = (list, scannedValue) => {
  if (!list || !Array.isArray(list)) return null

  const isObject = typeof scannedValue === 'object' && scannedValue !== null
  const rawValue = isObject ? scannedValue.value || '' : scannedValue || ''
  const upperVal = String(rawValue).trim().toUpperCase()

  // Support both legacy ID: and new ser: prefix
  const idMatch = upperVal.match(/(?:ID|SER):([^|]+)/i)
  const extractedId = idMatch ? idMatch[1] : null

  for (const item of list) {
    if (item.options && Array.isArray(item.options)) {
      // Grouped array format (e.g. comodities list)
      const match = item.options.find((opt) => {
        const sku = String(
          opt.sku || opt.SKU || opt.item?.SKU || opt.lotCode || opt.lot || '',
        )
          .trim()
          .toUpperCase()
        const label = String(opt.label || opt.nameItem || '')
          .trim()
          .toUpperCase()
        const value = String(
          opt.value || opt.id || opt.itemId || opt.ownerCode || opt.owner || '',
        )
          .trim()
          .toUpperCase()
        const optId = String(opt.id || opt.value || '')
        const optTrxId = String(opt.transactionId || '')

        // Match by new PM-Inventory format fields if available
        const isPmMatch =
          scannedValue?.type === 'PM-Inventory' &&
          ((scannedValue.serial &&
            (optId === String(scannedValue.serial) ||
              optTrxId === String(scannedValue.serial))) ||
            (scannedValue.productDescription &&
              label.includes(
                String(scannedValue.productDescription).toUpperCase(),
              )) ||
            (scannedValue.batch &&
              sku === String(scannedValue.batch).toUpperCase()))

        return (
          (sku && sku === upperVal) ||
          (label && label === upperVal) ||
          (value && value === upperVal) ||
          (extractedId &&
            (optId === extractedId || optTrxId === extractedId)) ||
          isPmMatch
        )
      })
      if (match) {
        return match
      }
    } else {
      // Flat array format (e.g. inventories/packings list)
      const sku = String(
        item.sku ||
          item.SKU ||
          item.item?.SKU ||
          item.lotCode ||
          item.lot ||
          '',
      )
        .trim()
        .toUpperCase()
      const label = String(item.label || item.nameItem || '')
        .trim()
        .toUpperCase()
      const value = String(
        item.value ||
          item.id ||
          item.itemId ||
          item.ownerCode ||
          item.owner ||
          '',
      )
        .trim()
        .toUpperCase()
      const itemId = String(item.id || item.value || '')
      const itemTrxId = String(item.transactionId || '')

      // Match by new PM-Inventory format fields if available
      const isPmMatch =
        scannedValue?.type === 'PM-Inventory' &&
        ((scannedValue.serial &&
          (itemId === String(scannedValue.serial) ||
            itemTrxId === String(scannedValue.serial))) ||
          (scannedValue.productDescription &&
            label.includes(
              String(scannedValue.productDescription).toUpperCase(),
            )) ||
          (scannedValue.batch &&
            sku === String(scannedValue.batch).toUpperCase()))

      if (
        (sku && sku === upperVal) ||
        (label && label === upperVal) ||
        (value && value === upperVal) ||
        (extractedId &&
          (itemId === extractedId || itemTrxId === extractedId)) ||
        isPmMatch
      ) {
        return item
      }
    }
  }

  return null
}

/**
 * Mock API service to simulate fetching commodity details by scanned QR ID
 */
const fetchMockCommodityByQr = async (scannedValue, moduleState) => {
  const isObject = typeof scannedValue === 'object' && scannedValue !== null
  const rawValue = isObject ? scannedValue.value || '' : scannedValue || ''

  return new Promise((resolve, reject) => {
    // Simulate network latency (e.g., 800ms)
    setTimeout(() => {
      if (String(rawValue).toLowerCase() === 'error') {
        reject(new Error('Barang tidak ditemukan di database API'))
        return
      }

      const { comodities, inventories, packings, adjustmentItems } =
        moduleState || {}

      let realItem = null
      let isInventory = false
      let isPacking = false

      // 1. Prioritize finding the exact item in physical inventory arrays
      if (inventories) {
        realItem = findItemBySku(inventories, scannedValue)
        if (realItem) isInventory = true
      }

      if (!realItem && packings) {
        realItem = findItemBySku(packings, scannedValue)
        if (realItem) {
          isInventory = true
          isPacking = true
        }
      }

      if (!realItem && adjustmentItems) {
        realItem = findItemBySku(adjustmentItems, scannedValue)
        if (realItem) isInventory = true
      }

      // 2. Fallback to general commodities if not found in stock
      if (!realItem && comodities) {
        realItem = findItemBySku(comodities, scannedValue)
      }

      if (realItem) {
        resolve({
          id: realItem.id || realItem.value || realItem.itemId,
          value: realItem.value || realItem.id || realItem.itemId,
          label: realItem.label || realItem.nameItem,
          sku:
            realItem.sku ||
            realItem.SKU ||
            realItem.item?.SKU ||
            String(rawValue).toUpperCase(),
          img:
            realItem.img || realItem.linkFoto || realItem.item?.linkFoto || '',
          inventoryId: isInventory ? realItem.id || realItem.value : undefined,
          groupId: isPacking ? realItem.id || realItem.value : undefined,
          isReal: true,
        })
        return
      }

      // 3. Fallback: Simulate completely new item from API
      resolve({
        id: `api-id-${rawValue}`,
        value: `api-val-${rawValue}`,
        label:
          isObject && scannedValue.productDescription
            ? scannedValue.productDescription
            : `Item [${String(rawValue).substring(0, 8)}${String(rawValue).length > 8 ? '...' : ''}]`,
        sku: String(rawValue).toUpperCase(),
        img: 'https://via.placeholder.com/150?text=Barang+API',
        inventoryId: undefined,
        isReal: false,
      })
    }, 800)
  })
}

/**
 * Focus the first visible input with a given name or placeholder pattern.
 */
const focusNextInput = (nameOrId) => {
  requestAnimationFrame(() => {
    const el =
      document.querySelector(`input[name="${nameOrId}"]`) ||
      document.querySelector(`input[id="${nameOrId}"]`) ||
      document.querySelector(`input[name="amount"]`) ||
      document.querySelector(`input[placeholder="0"]`)

    if (el) {
      el.focus()
      el.select()
    }
  })
}

/**
 * Extracts the product description/name from the scan result or raw value.
 */
const getProductNameFromScan = (scanResult) => {
  if (!scanResult) return null

  // 1. Check structured field from FormatProcessor
  if (scanResult.productDescription) return scanResult.productDescription

  // 2. Fallback: regex search in raw value for P=... or PD=...
  const rawValue = String(scanResult.value || '')
    .trim()
    .replace(/[\u0000-\u001F\u007F-\u009F\u200B-\u200D\uFEFF\u200E\u200F\u2000-\u200A]/g, '')

  // Look for P=... or PD=... at start of string or after pipes/parens
  // Support flexible spacing around = or :
  const match = rawValue.match(/(?:^|[|()]+)(?:P|PD)\s*[=:]\s*([^|()]+)/i)
  if (match) return match[1].trim()

  return null
}

/**
 * Dispatches a custom event with the product name so each page component
 * can handle it through its own React state management.
 */
const dispatchProductSearch = (productName) => {
  if (!productName) return

  const event = new CustomEvent('scanner:product-search', {
    detail: { productName },
  })
  document.dispatchEvent(event)
}

// ─── Transaction Module (Purchase / Sell Commodity) ──────────────────────

export const handleTransactionScan = async ({
  scanResult,
  dispatch,
  getState,
  enqueueSnackbar,
  pathname,
}) => {
  const productName = getProductNameFromScan(scanResult)

  if (productName) {
    dispatchProductSearch(productName)
    enqueueSnackbar(`🔍 Mencari "${productName}"...`, {
      variant: 'info',
      autoHideDuration: 2000,
    })
  } else {
    // Only use raw value if it's short and clean (not metadata QR)
    const raw = String(scanResult?.value || '').trim()
    const isMetadataQr = raw.includes('|') || raw.includes('(')
    if (raw && !isMetadataQr) {
      dispatchProductSearch(raw)
    }
  }

  dispatch(setScanStatus({ status: 'success' }))
}

// ─── Processing Module ──────────────────────────────────────────────────

export const handleProcessingScan = async ({
  scanResult,
  dispatch,
  getState,
  enqueueSnackbar,
}) => {
  const productName = getProductNameFromScan(scanResult)

  if (productName) {
    dispatchProductSearch(productName)
  } else {
    // Only use the raw value if it's short and clean (not metadata QR)
    const raw = String(scanResult?.value || '').trim()
    const isMetadataQr = raw.includes('|') || raw.includes('(')
    if (raw && !isMetadataQr) {
      dispatchProductSearch(raw)
    }
  }

  dispatch(setScanStatus({ status: 'success' }))
}

// ─── Inventory Movement Module ──────────────────────────────────────────

export const handleMovementScan = async ({
  scanResult,
  dispatch,
  getState,
  enqueueSnackbar,
  pathname,
}) => {
  const { value } = scanResult

  if (pathname.includes('form-transfer-inventory')) {
    // On the form page — dispatch custom event for the form component
    const event = new CustomEvent('scanner:transfer-scan', {
      detail: { scannedValue: value },
    })
    document.dispatchEvent(event)
    dispatch(setScanStatus({ status: 'success' }))
  } else if (pathname.match(/transfer-inventory\/\d/)) {
    // On detail page (arrival check) — highlight matching item
    const event = new CustomEvent('scanner:transfer-detail-scan', {
      detail: { scannedValue: value },
    })
    document.dispatchEvent(event)
    dispatch(setScanStatus({ status: 'success' }))
  } else {
    // On transfer inventory list page — trigger search
    let searchValue = getProductNameFromScan(scanResult)

    // Fallback to resolving via DB if no P value but we have a serial
    if (!searchValue && scanResult.value) {
      try {
        const state = getState()
        const moduleState = {
          inventories:
            state.sellCommodity?.inventories ||
            state.manageStock?.inventories ||
            [],
          packings:
            state.sellCommodity?.packings ||
            state.manageStock?.groupInventory ||
            [],
          comodities: state.sellCommodity?.comodities || [],
          adjustmentItems: state.orderAdjustment?.adjustmentItems || [],
        }

        dispatch(setScanStatus({ status: 'processing' }))
        const matched = await fetchMockCommodityByQr(scanResult, moduleState)
        if (matched && matched.isReal) {
          searchValue = matched.label || matched.sku
        }
      } catch (e) {
        console.error('Failed to resolve scan for movement search', e)
      }
    }

    // Final fallback to raw value ONLY if it's not a complex metadata QR
    if (!searchValue && value) {
      const isMetadataQr = value.includes('|') || value.includes('(')
      if (!isMetadataQr) searchValue = value
    }

    // GS1 Fragment safety check
    if (
      !searchValue ||
      (searchValue === value &&
        value.startsWith('(01)') &&
        !value.includes('|P='))
    ) {
      dispatch(setScanStatus({ status: 'idle' }))
      return
    }

    dispatchProductSearch(searchValue)

    // Logging for diagnostics
    if (localStorage.getItem('PMB_scannerDebug')) {
      // Debug mode active
    }
    dispatch(setScanStatus({ status: 'success' }))
  }
}

// ─── Context Router ─────────────────────────────────────────────────────

export const handleScanByContext = ({
  scanResult,
  dispatch,
  getState,
  enqueueSnackbar,
  pathname,
}) => {
  // Transaction module
  if (
    pathname.includes('beli') ||
    pathname.includes('jual') ||
    pathname.includes('purchase') ||
    pathname.includes('sell')
  ) {
    return handleTransactionScan({
      scanResult,
      dispatch,
      getState,
      enqueueSnackbar,
      pathname,
    })
  }

  // Processing module
  if (pathname.includes('process')) {
    return handleProcessingScan({
      scanResult,
      dispatch,
      enqueueSnackbar,
    })
  }

  // Inventory movement & inventory list module
  if (
    pathname.includes('transfer-inventory') ||
    pathname.includes('form-transfer-inventory') ||
    pathname.includes('inventory') ||
    pathname.includes('order-adjustment')
  ) {
    return handleMovementScan({
      scanResult,
      dispatch,
      getState,
      enqueueSnackbar,
      pathname,
    })
  }

  // No matching context — silently ignore or show info
  enqueueSnackbar(`Scan tidak didukung di halaman ini`, { variant: 'info' })
  dispatch(setScanStatus({ status: 'idle' }))
}
